/* VersionControlGridViewComponent.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;

const Renderer = require("./VersionControlGridViewRenderer");
const PagingVersionControlFeed = require("./PagingVersionControlFeed");
const DateColumn = require("./column_renderers/date/DateComponent");

const {
    VERSION_CONTROL_DISPLAY_TYPES,
    VERSION_CONTROL_CONSTRUCTS,
} = require("./VersionControlConstants");

class VersionControlGridViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        storageFactory,
        solutionsManager,
        modalUserFeedback,
        ConstructType
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._constructTypeEntityDef = ConstructType;
        this._constructTypes = undefined;
        this._sessionStorage = undefined;
        this._solutionsManager = solutionsManager;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "versionControlExplorer";

        this._storageFactory = storageFactory;
        this._userGridData = undefined;
        this._pagingVersionControlFeed = undefined;
        this._explorerState = {
            selectedIds: [],
        };
        this._sessionStorage = undefined;
        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    get columnConfig() {
        return {
            displayType: {
                label: "Item",
                priority: 1,
                width: 150,
                shown: true,
                options: VERSION_CONTROL_DISPLAY_TYPES,
                dataType: "string",
            },
            displayTypeName: {
                label: "Construct Type",
                priority: 2,
                width: 150,
                shown: true,
                options: this._constructTypes,
                dataType: "string",
            },
            displayFeatureType: {
                label: "Storage Type",
                priority: 3,
                width: 150,
                shown: true,
                dataType: "string",
            },
            displayFeatureInstance: {
                label: "Feature",
                priority: 4,
                width: 150,
                shown: true,
                dataType: "string",
            },
            name: {
                label: "Name",
                priority: 5,
                width: 300,
                shown: true,
                dataType: "string",
            },
            description: {
                label: "Description",
                priority: 6,
                shown: true, // Show by default while we hide staged
                width: 300,
                dataType: "string",
            },
            visibility: {
                label: "Visibility",
                priority: 7,
                width: 150,
                shown: true,
                dataType: "string",
            },
            updatedBy: {
                label: "Author",
                priority: 8,
                width: 200,
                shown: true,
                dataType: "string",
            },
            updatedOn: {
                label: "Date",
                priority: 9,
                width: 180,
                shown: true,
                dataType: "string",
                columnRendererComponent: DateColumn,
            },
        };
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _patchSorting(sorting) {
        if (typeof sorting === "string") {
            sorting = JSON.parse(sorting);
        }
        if (sorting && !sorting.name && this.columnConfig.name) {
            sorting.name = 1;
        }
        return sorting;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        return this._storageFactory
            .whenStorageIsReady()
            .then(() => {
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );
                this._sessionStorage = this._storageFactory.getSessionStorageInstance(
                    "entity_grid_gl",
                    `${this._gridKey}_explorerState`
                );
                this._explorerState = Object.assign(
                    {},
                    this._explorerState,
                    this._sessionStorage.getData()
                );
            })
            .then(() => {
                return this._loadConstructTypes();
            })
            .then(() => {
                const userSorting = this._userGridData.getSorting() || {};
                const patchedSort = this._patchSorting(userSorting);
                this._pagingVersionControlFeed = new PagingVersionControlFeed({
                    feedKey: this._gridKey + "_feed",
                    userSession: this._userSession,
                    columnConfig: this.columnConfig,
                    filter:
                        _.get(this._userGridData.getBasicSearchData(), "filterQuery", {}) ||
                        this._userGridData.getAdvancedSearchData(),
                    sort: patchedSort,
                    additionalDataFields: [],
                });
            })
            .then(() => {
                return this.whenFeedIsReady();
            });
    }

    shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();
        let shutdowns = [_.invoke(this._renderer, "shutdown") || Promise.resolve()];
        this.removeAllListeners();
        return Promise.all(shutdowns).then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._steveUtils = undefined;
                this._userSession = undefined;
                this._modalUserFeedback = undefined;
                this._constructTypeEntityDef = undefined;
                this._constructTypes = undefined;
                this._sessionStorage = undefined;
                this._solutionsManager = undefined;
                this._solutionName = undefined;
                this._gridKey = undefined;
                this._storageFactory = undefined;
                this._userGridData = undefined;
                this._pagingVersionControlFeed = undefined;
                this._explorerState = undefined;
                this._sessionStorage = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "VersionControlGridViewComponent.shutdown() error");
            }
        );
    }

    whenFeedIsReady() {
        return this._pagingVersionControlFeed.whenFeedReady();
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    get pagingFeed() {
        return this._pagingVersionControlFeed;
    }

    get storageFactory() {
        return this._storageFactory;
    }

    get selectedRows() {
        // TODO Must be replaced by local
        return this._renderer.selectedRows;
    }

    get activeOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get explorerState() {
        return this._explorerState;
    }

    set explorerState(val) {
        this.setExplorerState(val);
    }

    setExplorerState(val) {
        this._explorerState = val;
    }

    get userGridData() {
        return this._userGridData;
    }

    set userGridData(newData) {
        this.setUserGridData(newData);
    }

    setUserGridData(newData) {
        this._userGridData = newData;
    }

    updateExplorerState(property, val) {
        this._sessionStorage.setData(val, property);
        _.set(this._explorerState, property, val);
    }

    resetSelection(newEntityIds) {
        let selectedIds = [];

        if (newEntityIds && newEntityIds.length) {
            newEntityIds.forEach((newEntityId) => {
                selectedIds.push(newEntityId);
            });
        }

        this.renderer._versionControlEntityGrid.setSelectedRows(selectedIds);
        this.updateExplorerState("selectedIds", {selectedRows: selectedIds});
        this.renderer._renderAvailableActions();
    }

    loadAndScrollTo(entityId) {
        let that = this;
        this.pagingFeed.on("RequestedEntityFound", handleRequestedEntityFound);
        this.pagingFeed._requestEntity(entityId);

        function handleRequestedEntityFound(index) {
            that.renderer._versionControlEntityGrid.renderer.scrollToTheRow(index);
            that.pagingFeed.removeListener("RefreshView", handleRequestedEntityFound);
        }
    }

    openVersionControlInstanceEditor(selectedId, fullyQualifiedViewName) {
        if (!selectedId || !fullyQualifiedViewName) {
            return;
        }

        const config = this._pagingVersionControlFeed.getEntityById(selectedId);
        const title = config.name;

        this.updateExplorerState("openedEditor", {
            fqn: fullyQualifiedViewName,
            id: selectedId,
            title,
        });

        this._gameEditor.launchVersionControlEditor(
            {
                fqn: fullyQualifiedViewName,
            },
            {
                selectedId,
                title,
                config,
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    onUserSorted(sortData) {
        let sort = {};

        const sortCriteria =
            this.columnConfig[sortData.sortColumn].valuePath ||
            this.columnConfig[sortData.sortColumn].path ||
            sortData.sortColumn;

        sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        this._patchSorting(sort);
        this._pagingVersionControlFeed.setSort(sort);
    }

    _onSolutionChanged(solution) {
        this._solutionName = solution.name;
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _loadConstructTypes() {
        const constructTypeEntitiesList = await Promise.all(
            VERSION_CONTROL_CONSTRUCTS.map((construct) => {
                return this._constructTypeEntityDef.loadTypesAndDetails(
                    this.activeOrgId,
                    construct
                );
            })
        );

        // Flatten all returned arrays into one
        const constructTypeEntities = constructTypeEntitiesList.reduce(
            (acc, curr) => acc.concat(curr),
            []
        );

        const uniqueDisplayNames = new Set();
        const constructTypes = [];

        for (const entity of constructTypeEntities) {
            const hidden = _.get(entity, "pristineProperties.meta.hidden");
            if (!hidden) {
                const displayName = _.get(entity, "pristineProperties.meta.displayName");
                if (
                    displayName &&
                    !displayName.startsWith("(INTERNAL)") &&
                    !uniqueDisplayNames.has(displayName)
                ) {
                    uniqueDisplayNames.add(displayName);
                    constructTypes.push({
                        name: displayName,
                        title: displayName,
                    });
                }
            }
        }

        // Sort alphabetically by title
        this._constructTypes = constructTypes.sort((a, b) => a.title.localeCompare(b.title));
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getSelectedIds() {
        return _.get(this._explorerState, "selectedIds", []);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = VersionControlGridViewComponent;

VersionControlGridViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
    "$teve.entities.ConstructType",
];

VersionControlGridViewComponent.$displayDetails = {
    type: "View",
    name: "",
    icon: "versioncontrol.version_explorer",
    shown: false,
    order: 1,
};
